import { composeRefs } from '@volvo-cars/react-utils';
import type React from 'react';
import { forwardRef, useContext } from 'react';
import { RadioContext } from './radio-group';
import {
  type BaseSelectableCardProps,
  SelectableCardBase,
} from './selectable-card-base';

export interface RadioCardProps extends BaseSelectableCardProps {
  /**
   * The value that will be submitted with the form data if the radio is selected.
   *
   * @default 'on'
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/radio#data_representation_of_a_radio_group
   */
  value?: string;

  /**
   * Whether the input is disabled.
   */
  disabled?: boolean;

  /**
   * Fires if the input fails validation on form submit.
   */
  onInvalid?: React.FormEventHandler<HTMLInputElement>;

  /**
   * Focus event handler.
   */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * Blur event handler.
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * ARIA label for the input.
   */
  'aria-label'?: string;

  /**
   * ARIA describedby for the input.
   */
  'aria-describedby'?: string;

  /**
   * ARIA invalid state.
   */
  'aria-invalid'?: boolean;
}

export const RadioCard = forwardRef<HTMLInputElement, RadioCardProps>(
  function RadioCard(
    {
      value,
      title,
      meta,
      description,
      image,
      children,
      id,
      className,
      hidden,
      dir,
      lang,
      translate,
      slot,
      style,
      disabled,
      onInvalid,
      onFocus,
      onBlur,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      'aria-invalid': ariaInvalid,
      ...props
    }: RadioCardProps,
    ref: React.Ref<HTMLInputElement>,
  ) {
    const {
      value: groupValue,
      defaultValue: groupDefaultValue,
      ...groupProps
    } = useContext(RadioContext) ?? {};

    let checked: boolean | undefined;
    let defaultChecked: boolean | undefined;
    if (typeof groupValue !== 'undefined') {
      checked = value === groupValue;
    } else if (
      typeof groupDefaultValue !== 'undefined' &&
      value === groupDefaultValue
    ) {
      defaultChecked = true;
    }

    return (
      <SelectableCardBase
        title={title}
        meta={meta}
        description={description}
        image={image}
        id={id}
        className={className}
        hidden={hidden}
        dir={dir}
        lang={lang}
        translate={translate}
        slot={slot}
        style={style}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        renderInput={({
          inputId,
          effectiveAriaLabelledby,
          effectiveAriaDescribedby,
          inputRef,
        }) => (
          <input
            {...props}
            {...groupProps}
            ref={composeRefs(inputRef, ref)}
            type="radio"
            id={inputId}
            value={value}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onInvalid={onInvalid}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-label={ariaLabel}
            aria-labelledby={effectiveAriaLabelledby}
            aria-describedby={effectiveAriaDescribedby}
            aria-invalid={ariaInvalid}
          />
        )}
      >
        {children}
      </SelectableCardBase>
    );
  },
);
